import React, { useMemo } from "react";
import { Modal, Zoom, Box, Fade, Typography, keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  FINAL_REVEAL_STEP_ANSWER_REACTION_WAGER_INTRO,
  FINAL_REVEAL_STEP_WAGER_REACTION,
} from "../../gamestate/constants";

const GOLD = "#FFD700";

export const MODAL_WIDTH = 650;
export const MODAL_HEIGHT = 400;

const StyledModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ModalPaper = styled("div")(({ theme }) => ({
  width: `${MODAL_WIDTH}px`,
  minHeight: `${MODAL_HEIGHT}px`,
  padding: theme.spacing(4),
  color: "#ffffff",
  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 80%, #0f3460 100%)",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  border: "1px solid rgba(255,215,0,0.25)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 30px rgba(255,215,0,0.1)",
  position: "relative",
  overflow: "hidden",
}));

const PlayerHeader = styled(Box)({
  marginBottom: "24px",
});

const PlayerNameText = styled(Typography)({
  fontFamily: "Gyparody",
  fontSize: "2.5em",
  color: GOLD,
  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
});

const ScoreSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  marginTop: "8px",
});

const strikethroughAnim = keyframes`
  0% { text-decoration-color: transparent; }
  100% { text-decoration-color: #ff6b6b; }
`;

const slideInScore = keyframes`
  0% { opacity: 0; transform: translateX(-20px); }
  100% { opacity: 1; transform: translateX(0); }
`;

const PreFinalScore = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "crossedOut",
})(({ crossedOut }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: "1.4em",
  color: crossedOut ? "#999" : "#ffffff",
  textDecoration: crossedOut ? "line-through" : "none",
  textDecorationColor: crossedOut ? "#ff6b6b" : "transparent",
  textDecorationThickness: "3px",
  animation: crossedOut ? `${strikethroughAnim} 0.6s ease forwards` : "none",
  transition: "color 0.3s ease",
}));

const Arrow = styled("span")({
  fontSize: "1.4em",
  color: GOLD,
});

const FinalScoreText = styled(Typography)({
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: "1.6em",
  color: GOLD,
  animation: `${slideInScore} 0.5s ease 0.3s both`,
});

const ContentSection = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "16px",
});

const AnswerText = styled(Typography)({
  fontFamily: "Chilanka",
  fontSize: "2em",
  color: "#ffffff",
  padding: "16px",
});

const ResultBadge = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isCorrect",
})(({ isCorrect }) => ({
  fontSize: "1.3em",
  fontWeight: 700,
  color: isCorrect ? "#4caf50" : "#f44336",
}));

const WagerText = styled(Typography)({
  fontFamily: "'Poppins', sans-serif",
  fontSize: "1.6em",
  fontWeight: 600,
  color: GOLD,
});

/**
 * Determines what's visible based on the current reveal step.
 */
const getVisibility = (currentStep) => {
  const ANSWER_VISIBLE_STEPS = [
    FINAL_REVEAL_STEP_ANSWER_REACTION_WAGER_INTRO,
    FINAL_REVEAL_STEP_WAGER_REACTION,
  ];
  const RESULT_VISIBLE_STEPS = [
    FINAL_REVEAL_STEP_ANSWER_REACTION_WAGER_INTRO,
    FINAL_REVEAL_STEP_WAGER_REACTION,
  ];
  const WAGER_VISIBLE_STEPS = [FINAL_REVEAL_STEP_WAGER_REACTION];
  const CROSSOUT_STEPS = [FINAL_REVEAL_STEP_WAGER_REACTION];

  return {
    showAnswer: ANSWER_VISIBLE_STEPS.includes(currentStep),
    showResult: RESULT_VISIBLE_STEPS.includes(currentStep),
    showWager: WAGER_VISIBLE_STEPS.includes(currentStep),
    showCrossout: CROSSOUT_STEPS.includes(currentStep),
  };
};

/**
 * Modal for revealing a player's answer, wager, and score during Final Jeopardy.
 *
 * Driven entirely by `currentStep` — no internal click-through state.
 * Opens with a Zoom transition anchored at the player's tile position.
 *
 * @param {boolean} open - Whether the modal is visible
 * @param {Object} player - Player object (name, preFinalScore, finalScore)
 * @param {Object} submission - Player's submission (answer, wager, status)
 * @param {string} currentStep - Current finalReveal step
 * @param {Object} transformOrigin - { x, y } in modal-local pixel coords for the Zoom anchor, or null for center
 * @param {Function} onClose - Called when modal backdrop is clicked
 */
const FinalQuestionRevealerModal = ({
  open,
  player,
  submission,
  currentStep,
  transformOrigin,
  onClose,
}) => {
  const { showAnswer, showResult, showWager, showCrossout } = useMemo(
    () => getVisibility(currentStep),
    [currentStep]
  );

  if (!player) return null;

  const answer = submission?.answer || "";
  const wager = submission?.wager || 0;
  const isCorrect = submission?.result === "correct" || submission?.status === "correct";
  const preFinalScore = player.preFinalScore ?? 0;
  const finalScore = player.finalScore ?? (isCorrect ? preFinalScore + wager : preFinalScore - wager);

  const zoomStyle = transformOrigin
    ? { transformOrigin: `${transformOrigin.x}px ${transformOrigin.y}px` }
    : {};

  return (
    <StyledModal open={open} onClose={onClose} closeAfterTransition>
      <Zoom in={open} timeout={500} style={zoomStyle}>
        <ModalPaper>
          <PlayerHeader>
            <PlayerNameText>{player.name}</PlayerNameText>
            <ScoreSection>
              <PreFinalScore crossedOut={showCrossout}>
                ${preFinalScore.toLocaleString()}
              </PreFinalScore>
              {showCrossout && (
                <>
                  <Arrow>→</Arrow>
                  <FinalScoreText>${finalScore.toLocaleString()}</FinalScoreText>
                </>
              )}
            </ScoreSection>
          </PlayerHeader>

          <ContentSection>
            <Fade in={showAnswer} timeout={600}>
              <div>
                {answer ? (
                  <AnswerText>{answer}</AnswerText>
                ) : (
                  <AnswerText sx={{ fontStyle: "italic", opacity: 0.6 }}>
                    No answer
                  </AnswerText>
                )}
              </div>
            </Fade>

            <Fade in={showResult} timeout={400}>
              <div>
                <ResultBadge isCorrect={isCorrect}>
                  {isCorrect ? "✅ Correct!" : "❌ Incorrect"}
                </ResultBadge>
              </div>
            </Fade>

            <Fade in={showWager} timeout={600}>
              <div>
                <WagerText>Wager: ${wager.toLocaleString()}</WagerText>
              </div>
            </Fade>
          </ContentSection>
        </ModalPaper>
      </Zoom>
    </StyledModal>
  );
};

export { FinalQuestionRevealerModal };
